import axios from 'axios';
import { message } from 'antd';
import assign from 'lodash/assign';
import { loadFromLocalStorage, addToLocalStorage } from 'utils/localStorageHelpers';
import store from 'redux/store';
import user from 'redux/user';
import { history } from 'router';
import i18n from 'translation/i18n';

axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;

// A 401 from these means "wrong password" / "wrong code", not "the session is dead". Their
// screens report the error in place, so the session must be left alone.
const AUTH_ENDPOINTS = ['v1/clients/sign_in', 'v1/clients/two_factor_auth'];

const isAuthEndpoint = (url = '') => AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

// The token is unusable (expired, revoked by a password change, or orphaned because the
// account's uid changed with its email) and nothing on the page can recover. Drop the session
// and hand the client to sign-in with the reason, instead of leaving them in a signed-in shell
// where every screen is empty — an empty cart there reads as lost data, not as a lost session.
const endSession = (reason) => {
  // One page mount fires several requests at once and they all 401 together; the first one
  // clears authHeaders, so the rest fall out here instead of stacking redirects.
  if (!loadFromLocalStorage('authHeaders')) return;

  localStorage.removeItem('twoFactorAuth');
  localStorage.removeItem('showWelcomeMsg');
  store.dispatch(user.actions.signout(reason));
  history.replace('/clients/sign_in');
};

/* What a throttled client is told, and when.

   The rate limit answers any endpoint rather than one screen, and almost no
   thunk here keeps the rejection: only `extractPartsInfo` calls
   `rejectWithValue`, so `filterParts`, `fetchPromotions` and `fetchPartsCount`
   throw and leave `state.errors` undefined. A client over the catalogue budget
   watched the spinner stop on an empty listing with nothing said - the same
   silence, and the same wrong conclusion about the catalogue being empty, that
   the API side of this went to some trouble to remove.

   The API sends `Retry-After` on every 429 and names it in the CORS expose list
   for this: without it the browser cannot read the header at all, and a screen
   has no way to say how long the wait is. */
const MINUTE = 60;

// Below three minutes the count is dropped rather than rendered. The Arabic
// plural for 3-10 is the one `too many requests for minutes` is written in, so
// a `1` or a `2` would be printed into the wrong form - and a wait that short
// is what "بعد قليل" already says.
const MINUTES_WORTH_NAMING = 3;

export const throttleMessage = (error) => {
  const seconds = Number(error.response?.headers?.['retry-after']);
  const minutes = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / MINUTE) : 0;

  if (minutes >= MINUTES_WORTH_NAMING) return i18n.t('alert:too many requests for minutes', { minutes });

  // The server's own wording when it sent one, so the portal and the API do not
  // say this differently; the local string covers a 429 from anything in front
  // of the app, which answers in its own format or in none.
  const fromServer = error.response?.data?.errors;

  return typeof fromServer === 'string' && fromServer.trim() ? fromServer : i18n.t('alert:too many requests');
};

/* One page mount fires several requests together, so they come back throttled
   together - a toast each, stacked, saying the same thing. The 401 path has the
   same problem and solves it by the first one clearing `authHeaders`; there is
   nothing equivalent to clear here, so the notice is simply not repeated while
   one is still on screen. */
const NOTICE_SECONDS = 6;
let noticeShownAt = 0;

const announceThrottle = (error) => {
  const now = Date.now();
  if (now - noticeShownAt < NOTICE_SECONDS * 1000) return;

  noticeShownAt = now;
  message.error(throttleMessage(error), NOTICE_SECONDS);
};

const injectAuthHeaders = (request) => {
  const authHeaders = loadFromLocalStorage('authHeaders');
  assign(request.headers, authHeaders);
  return request;
};

axios.interceptors.request.use(injectAuthHeaders);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 429) announceThrottle(error);

    if (error.response?.status === 401 && !isAuthEndpoint(error.config?.url)) {
      const { two_factor_required, account_inactive, error: message } = error.response.data || {};

      if (two_factor_required) {
        // OTP session expired mid-use: send the client back to the OTP screen, which
        // requests a fresh code on mount. Not a full re-login.
        addToLocalStorage('twoFactorAuth', true);
        store.dispatch(user.actions.setErrors(''));
        history.push('/clients/two_factor_authentication');
      } else {
        // The account being switched offline has its own explanation; everything else is
        // just an expired session.
        endSession(account_inactive ? message : 'login again');
      }
    }
    return Promise.reject(error);
  },
);

export default axios;
